/**
 * Modal gallery showing built-in starter templates plus any user/company
 * templates fetched from the API. Picking one applies its `chart_type` and
 * `config` snapshot to the builder, then closes.
 *
 * Templates are loaded lazily — the API call only fires the first time the
 * modal opens. Built-ins are always shown; custom templates are appended
 * once the fetch resolves.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  PieChart,
  LineChart,
  CircleDot,
  Gauge,
  Table,
  Activity,
  LayoutGrid,
  Grid2X2,
  Waves,
  Globe2,
  StickyNote,
  Sparkles,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  reportingAPI,
  type IndicatorBuilderConfig,
  type ReportTemplate,
} from '../../../lib/api/reporting';
import type { BuilderChartType } from './ChartTypePicker';
import { STARTER_TEMPLATES, type StarterTemplate } from './starterTemplates';

const ICON_MAP: Record<string, LucideIcon> = {
  BarChart3,
  PieChart,
  LineChart,
  CircleDot,
  Gauge,
  Table,
  Activity,
  LayoutGrid,
  Grid2X2,
  Waves,
  Globe2,
  StickyNote,
  Sparkles,
};

function iconFor(name: string | undefined): LucideIcon {
  if (name && ICON_MAP[name]) return ICON_MAP[name];
  return Sparkles;
}

interface GalleryEntry {
  key: string;
  name: string;
  description: string;
  chart_type: BuilderChartType;
  icon: LucideIcon;
  config: IndicatorBuilderConfig;
  scope: 'builtin' | 'company' | 'user';
  /** Backend ID when scope ∈ {company,user}; undefined for hard-coded built-ins. */
  templateId?: number;
}

function builtinToEntry(t: StarterTemplate): GalleryEntry {
  return {
    key: `builtin:${t.id}`,
    name: t.name,
    description: t.description,
    chart_type: t.chart_type,
    icon: iconFor(t.icon),
    config: t.config,
    scope: 'builtin',
  };
}

function customToEntry(t: ReportTemplate): GalleryEntry {
  return {
    key: `custom:${t.id}`,
    name: t.name,
    description: t.description,
    chart_type: t.chart_type as BuilderChartType,
    icon: iconFor(t.icon_name),
    config: t.config as IndicatorBuilderConfig,
    scope: t.scope as 'company' | 'user',
    templateId: t.id,
  };
}

interface TemplatesGalleryProps {
  open: boolean;
  onClose: () => void;
  onApply: (chartType: BuilderChartType, config: IndicatorBuilderConfig) => void;
}

export function TemplatesGallery({ open, onClose, onApply }: TemplatesGalleryProps) {
  const [customTemplates, setCustomTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    reportingAPI
      .listTemplates()
      .then((res) => {
        if (cancelled) return;
        setCustomTemplates(res.data.results || []);
      })
      .catch(() => {
        if (cancelled) return;
        // Custom templates are non-essential — silently ignore so built-ins
        // still render even if the API is unavailable.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const entries = useMemo<GalleryEntry[]>(() => {
    return [
      ...STARTER_TEMPLATES.map(builtinToEntry),
      ...customTemplates.map(customToEntry),
    ];
  }, [customTemplates]);

  const handleApply = (entry: GalleryEntry) => {
    onApply(entry.chart_type, entry.config);
    onClose();
  };

  const handleDelete = async (entry: GalleryEntry) => {
    if (entry.templateId === undefined) return;
    if (!window.confirm(`Delete template "${entry.name}"?`)) return;
    try {
      await reportingAPI.deleteTemplate(entry.templateId);
      setCustomTemplates((prev) => prev.filter((t) => t.id !== entry.templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Start from a Template</DialogTitle>
          <DialogDescription>
            Pick a pre-configured chart and tweak it from there. You can save your own setups
            as templates with the "Save as Template" button.
          </DialogDescription>
        </DialogHeader>

        {loading && customTemplates.length === 0 && (
          <p className="text-xs text-muted-foreground">Loading saved templates…</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {entries.map((entry) => {
            const Icon = entry.icon;
            return (
              <div
                key={entry.key}
                className="border rounded-md p-3 hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-primary/10 text-primary p-2 shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold truncate">{entry.name}</h3>
                      {entry.scope !== 'builtin' && (
                        <Badge variant="secondary" className="text-[10px] uppercase">
                          {entry.scope}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-3 mb-2">
                      {entry.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-7 text-xs" onClick={() => handleApply(entry)}>
                        Use this
                      </Button>
                      {entry.templateId !== undefined && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(entry)}
                          aria-label={`Delete ${entry.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TemplatesGallery;
